import type { ClipboardEvent } from "react";
import { lineBeforeCaret, quotePaste } from "@bloom/shared";

/**
 * A paste into the message box (#657): with the caret on a quote line, a paste of several lines keeps every line in the quote
 * (`quotePaste`, shared/src/quotePaste.ts). Returns true when it handled the paste; false leaves it to the browser.
 *
 * The text goes in as a browser edit (`insertText`), so one Undo takes the whole paste back and the input event behaves as
 * for a paste. Where the browser has no such command, `setValue` gets the whole new text.
 */
export function pasteQuoted(e: ClipboardEvent<HTMLTextAreaElement>, setValue: (value: string) => void): boolean {
  const raw = e.clipboardData.getData("text/plain");
  const el = e.currentTarget;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const quoted = quotePaste(lineBeforeCaret(el.value, start), raw);
  if (quoted === raw) return false;
  e.preventDefault();
  const expected = el.value.slice(0, start) + quoted + el.value.slice(end);
  let done = false;
  try {
    el.focus();
    done = typeof document.execCommand === "function" && document.execCommand("insertText", false, quoted);
  } catch {
    done = false;
  }
  if (!done || el.value !== expected) {
    setValue(expected);
    const caret = start + quoted.length;
    el.setSelectionRange(caret, caret);
    // A render that follows can move the caret: put it back once it is done.
    requestAnimationFrame(() => el.setSelectionRange(caret, caret));
  }
  return true;
}
